import { Button, Flex, Space, Table, Tag, Row, Col, Input } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import styles from "./ActiveSubscription.module.scss";
import { colors } from "../../../../../../constants/colors";
import { useSelector } from "react-redux";
import { getSuperAdminSubscriptionAction } from "../../../../../../redux/services/super-admin-subscriptions/superAdminSubscriptions";
import { routes } from "../../../../../../constants";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";
import { EditIcon } from "../../../../../../assets/svg";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";

const ActiveSubscription = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { superAdminSubscription } = useSelector(
    (state) => state.superAdminSubscription
  );
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: superAdminSubscription?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      status: "active",
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getSuperAdminSubscriptionAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.AutoCredit.subscriptionType"),
        render: (_, record) => (
          <p>
            {record?.type !== "main"
              ? record?.type.toUpperCase()
              : record?.subscription_plan?.code ?? "-"}
          </p>
        ),
      },
      {
        title: t("AppModuleNames.SuperAdmin.ServiceProvider"),
        dataIndex: "organization_name",
        key: "organization_name",
      },
      {
        title: t("Modules.addOns.duration"),
        dataIndex: "duration",
        key: "duration",
        render: (_, record) => (
          <p>{`${record?.duration} ${record?.duration_unit}`}</p>
        ),
      },
      {
        title: t("servicesList.price"),
        dataIndex: "charges",
        key: "charges",
        render: (_, record) => <p>{`${record?.price} ${record?.currency?.toUpperCase()}`}</p>,
      },
      {
        title: t("CommonUtils.logins"),
        dataIndex: "logins",
        key: "logins",
        render: (_, record) => <p>{record?.type === "main" ? record?.default_logins : record?.type === "addon" ? record?.addon_logins : 0}</p>
      },
      {
        title: t("activeSubs.dateFrom"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => {
          const date = dayjs(record?.created_at).format("DD-MM-YYYY");
          return <p>{date}</p>;
        },
      },
      {
        title: t("activeSubs.dateTo"),
        dataIndex: "expire_date_time",
        key: "expire_date_time",
        render: (_, record) => {
          const date = dayjs(record?.expire_date_time).format("DD-MM-YYYY");
          return <p>{date}</p>;
        },
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  margin: "auto",
                  background:
                    record?.status === "active"
                      ? colors.lightTeal
                      : colors.lightPinkishRed,
                  color:
                    record?.status === "active"
                      ? colors.darkTeal
                      : colors.darkMaroon,
                }}
              >
                {record?.status === "active" ? t("CommonUtils.active") : t("CommonUtils.inactive")}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("activeSubs.edit"),
        render: (_, record) => {
          const handleUpdate = (e, record) => {
            navigate(
              baseRouteMakerForSuperAdmin(routes.ACTIVE_SUBSCRIPTION_UPDATE),
              {
                state: record,
              }
            );
          };
          return (
            <>
            {
              record?.type === "main" ?
              <Space size="middle">
                <Button
                  type="text"
                  size="medium"
                  icon={<EditIcon fillColor={colors.slateGray} />}
                  onClick={(e) => handleUpdate(e, record)}
                />
              </Space>
              :
              <>-</>
            }
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* initial dependencies rendering */

  return (
    <div className={styles.activeSubscriptionContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByServiceProvider")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
        </Row>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={superAdminSubscription?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default ActiveSubscription;
